import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const HeroSlider = ({ introComplete = false }) => {
  const [initialDelay, setInitialDelay] = useState(!introComplete);

  const autoplayPlugin = Autoplay({
    delay: initialDelay ? 4000 : 5000, // First slide plays for 4s after intro (2s intro + 2s pause)
    stopOnInteraction: false
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      duration: 30,
      skipSnaps: false
    },
    [autoplayPlugin]
  );

  useEffect(() => {
    if (introComplete && initialDelay) {
      // After intro completes, pause for 2 seconds on first slide
      setTimeout(() => {
        setInitialDelay(false);
        // Reset autoplay with normal delay
        if (emblaApi) {
          emblaApi.reInit();
        }
      }, 2000);
    }
  }, [introComplete, initialDelay, emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const slides = [
  {
    id: 1,
    title: "ADC PART 2 EXCELLENCE",
    subtitle: "MASTER THE AUSTRALIAN DENTAL COUNCIL PRACTICAL EXAM",
    description: "Comprehensive 35-day intensive training with 3-5 mock examinations and expert guidance",
    buttonText: "Explore ADC Course",
    buttonLink: "#adc-details",
    backgroundImage: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    video: "https://customer-assets.emergentagent.com/job_oda-courses/artifacts/0kmjhlii_download_20260509_001549_0000.mp4"
  },
  {
    id: 2,
    title: "NDECC CERTIFICATION",
    subtitle: "Your Gateway to Canadian Dental Practice",
    description: "Expert training for National Dental Examining Committee of Canada exam with realistic simulation",
    buttonText: "Learn About NDECC",
    buttonLink: "#ndecc-details",
    backgroundImage: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
  },
  {
    id: 3,
    title: "WORLD-CLASS TRAINING FACILITY",
    subtitle: "State-of-the-Art Dental Simulation Lab",
    description: "Original ADC manikins, CPR training, and exact exam simulation setup in the heart of Chandigarh",
    buttonText: "Tour Our Facility",
    buttonLink: "#facility-details",
    backgroundImage: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)"
  },
  {
    id: 4,
    title: "13+ YEARS OF EXCELLENCE",
    subtitle: "Join 1000+ Successful Dentists",
    description: "Proven track record with 95%+ success rate across ADC, NDECC, Irish & US dental exams",
    buttonText: "Read Success Stories",
    buttonLink: "#success-details",
    backgroundImage: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)"
  }];


  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {slides.map((slide, index) =>
          <div key={slide.id} className="flex-[0_0_100%] min-w-0 relative">
              <div
              className="relative h-[600px] flex items-center"
              style={{
                background: slide.backgroundImage
              }}>

                {/* Video Background for First Slide */}
                {slide.video &&
              <div className="absolute inset-0 overflow-hidden">
                    <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover opacity-30">

                      <source src={slide.video} type="video/mp4" />
                    </video>
                  </div>
              }

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40"></div>

                {/* Content */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                  <div className="max-w-3xl text-white">
                    <h1 className="hero-heading text-5xl md:text-6xl lg:text-7xl font-black mb-4 leading-tight animate-fadeIn">
                      {slide.title}
                    </h1>
                    <h2 className="text-2xl md:text-3xl mb-6 text-gray-100 font-semibold animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                      {slide.subtitle}
                    </h2>
                    <p className="text-lg md:text-xl mb-8 text-gray-200 max-w-2xl animate-fadeIn" style={{ animationDelay: '0.4s' }}>
                      {slide.description}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn" style={{ animationDelay: '0.6s' }}>
                      <Button
                      asChild
                      size="lg"
                      className="bg-white text-[#1e3a8a] hover:bg-gray-100 text-lg px-8 py-6 font-bold">

                        <a href={slide.buttonLink}>{slide.buttonText}</a>
                      </Button>
                      <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 font-bold">

                        <Link to="/contact">Contact Us</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
        aria-label="Previous slide">

        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
        aria-label="Next slide">

        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-3">
        {slides.map((_, index) =>
        <button
          key={index}
          onClick={() => emblaApi?.scrollTo(index)}
          className={`transition-all duration-300 rounded-full ${
          index === selectedIndex ?
          'bg-white w-12 h-3' :
          'bg-white/50 hover:bg-white/70 w-3 h-3'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>
    </div>);

};

export default HeroSlider;